import { Link } from 'react-router-dom';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { heroTake } from '../../domain/cars/carSchema';
import { LockIcon } from '../icons/ControlIcons';
import { artSource } from './carArt';

/**
 * A car's own screen, before it has been won (SPEC-010 PR-10.6).
 *
 * Reachable three ways and each of them matters: a deep link the reader typed,
 * a share somebody sent them, and the address bar after a locked card. The
 * middle one is the reason this is an INVITATION rather than an error — the
 * whole point of the ceremony's share is that a winner sends this address to
 * somebody who has not won the car, and landing them on a not-found screen or
 * bouncing them to the garage would waste the one share the product asks its
 * readers to make.
 *
 * It is the locked card's composition at screen size and not a new drawing: the
 * same silhouette, the same lock, the same withheld name. A reader who has seen
 * the shelf recognises what this is before they read the sentence under it.
 *
 * A separate component from `CarDetailScreen` rather than a branch inside it,
 * because that file is a composition of nine bands and already close to the
 * 350-line rule — and because this screen shares nothing with it but the
 * address.
 */
export default function CarLockedPanel({ car }) {
    const { localeCode } = useLocale();
    const t = useTranslate();
    // The side profile, for the reason the card beside it takes one: a front
    // view carries its own ground, and a silhouette of a ground is a rectangle.
    const art = artSource(car, heroTake(car));

    return (
        <section className="car-detail car-locked">
            <div className="car-locked__stage">
                {art ? (
                    <img
                        className="car-locked__art"
                        src={art.src}
                        srcSet={art.srcSet}
                        sizes="(min-width: 640px) 600px, 100vw"
                        /* Deliberately reduced to a shape, and the heading below
                           says what the shape is. "A black rectangle" announced
                           would be worse than silence. */
                        alt=""
                        fetchPriority="high"
                        decoding="async"
                    />
                ) : null}

                <span className="car-locked__lock" aria-hidden="true">
                    <LockIcon size={36} />
                </span>

                <span className={`rarity-ribbon rarity-ribbon--${car.rarity} car-locked__rarity`}>
                    {t(`cars.rarity.${car.rarity}`)}
                </span>
            </div>

            <span className="car-locked__hairline heat-hairline" aria-hidden="true" />

            <div className="car-locked__body">
                {/* The year is the one fact this screen gives away, because it
                    is the one that makes somebody want the card without telling
                    them which card it is. */}
                <p className="car-locked__year figure">{car.year}</p>
                <h1 className="car-locked__title">{t('cars.locked.title')}</h1>
                <p className="car-locked__note">{t('cars.locked.body')}</p>

                <Link className="button button--primary car-locked__action" to={`/${localeCode}/duel`}>
                    {t('cars.locked.action')}
                </Link>

                <Link className="button button--quiet" to={`/${localeCode}/garage`}>
                    {t('cars.backToGarage')}
                </Link>
            </div>
        </section>
    );
}
